import { useEffect, useState } from "react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { FileManager } from "~/api/fileManager";
import { PaginatedTable } from "~/components/Shared/PaginatedTable";
import { useDownloadFile } from "~/hooks/useGetFileManager";
import { ROUTES } from "~/router";
import { DropdownMenu } from "~/ui";

interface FileManagerTableProps {
  fileManager: FileManager[];
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (val: number) => void;
  totalItems?: number;
  pageSize: number;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
}

export const MODAL_TYPE_OPTIONS = {
  DELETE: "delete",
  EDIT: "edit",
} as const;

export type ModalType =
  (typeof MODAL_TYPE_OPTIONS)[keyof typeof MODAL_TYPE_OPTIONS];

export const FileManagerTable = ({
  fileManager,
  isLoading,
  currentPage,
  setCurrentPage,
  totalItems,
  pageSize,
  sorting,
  setSorting,
}: FileManagerTableProps) => {
  const { t } = useTranslation();
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  const { data: downloadData, isDownloadingFile } = useDownloadFile(
    selectedFileId ?? 0,
  );

  useEffect(() => {
    if (downloadData?.temporary_url && selectedFileId) {
      window.open(downloadData.temporary_url, "_blank");
      setSelectedFileId(null);
    }
  }, [downloadData, selectedFileId]);

  const handleDownload = (fileId: number) => {
    setSelectedFileId(fileId);
  };

  const columns: ColumnDef<FileManager>[] = [
    {
      header: t("file_manager.name"),
      accessorKey: "name",
    },
    {
      header: t("file_manager.patient"),
      accessorKey: "patient",
      cell: ({ row }) => {
        const patient = row.original.patient;
        const caregiver = row.original.caregiver;

        if (patient) {
          return (
            <Link
              to={ROUTES.patientDetail.replace(":id", patient.id.toString())}
              className="text-primary-40 underline dark:text-primary-80"
            >
              {patient.name} {patient.surname}
            </Link>
          );
        }

        if (caregiver) {
          return (
            <span>
              {caregiver.name} {caregiver.surname}
            </span>
          );
        }

        return "-";
      },
      enableSorting: false,
    },
    {
      header: t("file_manager.type"),
      accessorKey: "file_type",
      enableSorting: false,
    },
    {
      header: t("file_manager.created_at"),
      accessorKey: "created_at",
      cell: ({ row }) => {
        return new Date(row.original.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      },
    },
    {
      id: "actions",
      cell: ({ row: { original } }) => {
        const isCurrentFileDownloading =
          isDownloadingFile && selectedFileId === original.id;
        return (
          <div className="flex justify-end">
            <DropdownMenu
              options={[
                {
                  label: isCurrentFileDownloading
                    ? t("file_manager.downloading")
                    : t("file_manager.download"),
                  onClick: () =>
                    !isCurrentFileDownloading && handleDownload(original.id),
                },
              ]}
              buttonAriaLabel={t("file_manager.actions")}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PaginatedTable
        data={fileManager}
        columns={columns}
        isLoadingData={isLoading}
        pageSize={pageSize}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalRows={totalItems}
        sorting={sorting}
        setSorting={setSorting}
      />
    </>
  );
};
